import {Injectable} from '@angular/core';
import {LoginPayload} from './payload/login.payload';
import {environment} from '../../../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {LoginResponse} from '../../impl/response/login.response';
import {User} from '../../../account/modules/user/api/entity/user.model';
import {Account} from '../../../../shared/model/account/account.model';
import {AccountService} from '../../../../services/account/account.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {NavController} from '@ionic/angular';
import {LoginResult} from '../../../../services/login/loginResult';
import {SimpleCacheService} from '../../../common/cache/api/simpleCache.service';
import {UserConverter} from '../../../account/modules/user/api/converter/user.converter';
import {AccountConverter} from '../../../account/common/api.converter/Account.converter';
import {JwtTokenModel} from './jwtToken.model';
import {
    allApplications, ApplicationInsoleSelfProduction, ApplicationInsoleSubContracting, Applications,
    AuthApplicationModel
} from './authApplication.model';
import {PublicLoginResponse} from '../../impl/response/publicLogin.response';
import {Router} from "@angular/router";
import {ApplicationCache} from "../../../common/cache/api/application.cache";

@Injectable({providedIn: 'root'})
export class AuthService {

    private static readonly STORAGE_KEY_ACCESS_TOKEN = 'ACCESS_TOKEN';
    private static readonly STORAGE_KEY_TOKEN = 'TOKEN';
    private static readonly STORAGE_KEY_USER = 'USER';
    private static readonly STORAGE_KEY_ACCOUNT = 'ACCOUNT';
    private static readonly STORAGE_KEY_EXTERNAL_ACCESS_ACCOUNT_BUSINESS_CODE = 'EXTERNAL_ACCESS_ACCOUNT_BUSINESS_CODE';

    public tokenContent$: Observable<JwtTokenModel> = new BehaviorSubject(undefined);
    public tokenChanged$: Observable<boolean> = new BehaviorSubject(false);
    public applications$: Observable<AuthApplicationModel[]> = new BehaviorSubject([]);

    // In case of Account access
    // Deprecated use tokenContent$ instead
    public user: Observable<User> = new BehaviorSubject(undefined);

    // Deprecated use tokenContent$ instead
    public account: Observable<Account> = new BehaviorSubject(undefined);
    private accessToken: string;

    constructor(private httpClient: HttpClient,
                private accountService: AccountService,
                private navController: NavController,
                private router: Router,
                private simpleCacheService: SimpleCacheService,
                private applicationCache: ApplicationCache,
                private userConverter: UserConverter) {
        const userStorage = this.userStored;
        if (userStorage) {
            this.updateUser(userStorage);
        }

        const accountStorage = this.accountStored;
        if (accountStorage) {
            (this.account as BehaviorSubject<Account>).next(accountStorage);
        }

        const tokenStorage = this.tokenStored;
        // console.log('token stored ' + tokenStorage);
        if (tokenStorage) {
            try {
                // console.log('get token content from storage ' + JwtTokenModel.fromJson(tokenStorage));
                this.readToken(tokenStorage, false);
            } catch (e) {
                // console.log('Stored token format is not correct');
            }
        }

        const accessTokenStorage = this.accountStored;
        if (accessTokenStorage) {
            this.accessToken = tokenStorage;
        }
    }

    get userSnapshot(): User {
        return (this.user as BehaviorSubject<User>).getValue();
    }

    get accountSnapshot(): Account {
        return (this.account as BehaviorSubject<Account>).getValue();
    }

    get tokenContentSnapshot(): JwtTokenModel {
        return (this.tokenContent$ as BehaviorSubject<JwtTokenModel>).getValue();
    }

    get applicationsSnapshot(): AuthApplicationModel[] {
        return (this.applications$ as BehaviorSubject<AuthApplicationModel[]>).getValue();
    }

    getStorageValue(storageKey: string): string {
        try {
            return window.localStorage.getItem(storageKey);
        } catch (e) {
            console.log(e);
            return undefined;
        }
    }

    private removeStorageValue(storageKey: string): void {
        try {
            window.localStorage.removeItem(storageKey);
        } catch (e) {
            console.log(e);
        }
    }

    private setStorageValue(storageKey: string, value: any): void {
        try {
            window.localStorage.setItem(storageKey, value);
        } catch (e) {
            console.log(e);
        }
    }

    public changeAccount(accountBusinessCode: string) {
        this.simpleCacheService.clear();
        this.applicationCache.clear();
        return this.handleLoginResponse(
            this.httpClient.post<LoginResponse>(environment.webserviceUrl + '/v1/auth/switch-account/' + accountBusinessCode, {})
                .toPromise(), false);
    }

    public login(payload: LoginPayload, redirectToOldApplication = true): Promise<void> {
        this.simpleCacheService.clear();
        return this.handleLoginResponse(
            this.httpClient.post<LoginResponse>(environment.webserviceUrl + '/v1/auth/login', payload)
                .toPromise(), redirectToOldApplication);
    }

    public loginWithToken(token: string) {
        this.simpleCacheService.clear();
        return this.handleLoginResponse(
            this.httpClient.post<LoginResponse>(environment.webserviceUrl + '/v1/auth/login-from-token', {
                token: token
            })
                .toPromise(), false);
    }

    public publicLogin(accountBusinessCode: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.httpClient.post<PublicLoginResponse>(environment.webserviceUrl + '/v1/auth/public-login', {
                accountBusinessCode
            }).toPromise()
                .then((result: PublicLoginResponse) => {
                    this.readToken(result.token, true);
                    const account = new Account();
                    account.summary = result.account;
                    this.storeAccount(account);
                    resolve();
                }).catch((e) => reject(e));
        });
    }

    private get userStored(): User {
        const userStorage = this.getStorageValue(AuthService.STORAGE_KEY_USER);
        return userStorage ? this.userConverter.convert(JSON.parse(userStorage)) : undefined;
    }

    private get accountStored(): Account {
        const accountStorage = this.getStorageValue(AuthService.STORAGE_KEY_ACCOUNT);
        return accountStorage ? new AccountConverter().convert(JSON.parse(accountStorage)) : undefined;
    }

    private get storedExternalAccountBusinessCode(): string {
        return this.getStorageValue(AuthService.STORAGE_KEY_EXTERNAL_ACCESS_ACCOUNT_BUSINESS_CODE);
    }

    private get tokenStored(): string {
        return this.getStorageValue(AuthService.STORAGE_KEY_TOKEN);
    }

    private get accessTokenStored(): string {
        return this.getStorageValue(AuthService.STORAGE_KEY_ACCESS_TOKEN);
    }

    public logout(redirectToLogin: boolean): void {
        console.log('logout');
        this.simpleCacheService.clear();

        if (this.accountStored) {
            this.removeStorageValue(AuthService.STORAGE_KEY_ACCOUNT);
            (this.account as BehaviorSubject<Account>).next(undefined);
        }

        if (this.userStored) {
            this.removeStorageValue(AuthService.STORAGE_KEY_USER);
            (this.user as BehaviorSubject<User>).next(undefined);
        }

        if (this.accessTokenStored) {
            this.removeStorageValue(AuthService.STORAGE_KEY_ACCESS_TOKEN);
            this.accessToken = undefined;
        }

        if (this.tokenStored) {
            this.removeStorageValue(AuthService.STORAGE_KEY_TOKEN);
            (this.tokenContent$ as BehaviorSubject<JwtTokenModel>).next(undefined);
        }
        if (redirectToLogin === true) {
            this.navController.navigateRoot('authentication/login');
        }
    }

    public handleLoginResponse(promise: Promise<LoginResult>, redirectTOOldApplication: boolean) {
        console.log('handleLoginResponse');
        return new Promise<void>((resolve, reject) => {
            promise
                .then((result) => {
                    this.readToken(result.token, true);
                    console.log('token read');
                    this.setStorageValue(AuthService.STORAGE_KEY_ACCESS_TOKEN, result.accessToken);
                    this.accessToken = result.accessToken;

                    this.loadUserAndAccount()
                        .then(() => {
                            resolve();
                            if (redirectTOOldApplication === true) {
                                // console.log(environment.production + ' ' + environment.oldFrontendApplication);
                                let targetUrl;
                                if (this.tokenContentSnapshot.currentProvider?.providerBusinessCode === 'PRINSOLES') {
                                    targetUrl = 'app/insole/production/kanban';
                                }
                                if (targetUrl) {
                                    this.navController.navigateRoot(targetUrl);
                                } else {
                                    this.navController.navigateRoot('app/home');
                                }
                            }

                        })
                        .catch((e) => reject(e));

                })
                .catch((e) => {
                    reject(e);
                });
        });
    }

    generateOneTimeCode(email: string, accountBusinessCode: string, recaptchaToken: string): Promise<void> {
        return this.httpClient.post<void>(environment.webserviceUrl + '/v1/auth/one-time-code/generate', {
            email,
            accountBusinessCode,
            recaptchaToken,
        }).toPromise();
    }

    externalLogin(email: string, code: string, recaptchaToken: string): Promise<JwtTokenModel> {
        return new Promise<JwtTokenModel>((resolve, reject) => {
            this.httpClient.post<PublicLoginResponse>(environment.webserviceUrl + '/v1/auth/external-login', {
                email,
                code,
                recaptchaToken
            }).toPromise()
                .then((response) => {
                    this.logout(false);
                    const tokenObject = this.readToken(response.token, true);
                    const account = new Account();
                    account.summary = response.account;
                    this.storeAccount(account);
                    resolve(tokenObject);
                })
                .catch((e) => reject(e));
        });
    }

    externalChangePartner(partnerBusinessCode): Promise<JwtTokenModel> {
        return new Promise<JwtTokenModel>((resolve, reject) => {
            this.httpClient.post<string>(environment.webserviceUrl + '/v1/auth/external-login/change/partner/' + partnerBusinessCode, {}, {
                responseType: 'text' as 'json'
            }).toPromise()
                .then((token) => {
                    this.logout(false);
                    const tokenObject = this.readToken(token, true);
                    resolve(tokenObject);
                })
                .catch((e) => reject(e));
        });
    }

    private loadUserAndAccount(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.loadUser()
                .then((user) => {
                    this.loadAccount()
                        .then((account) => {
                            resolve();
                        })
                        .catch((e) => reject(e));
                })
                .catch(e => reject(e));
        });
    }

    private loadUser(): Promise<User> {
        return new Promise<User>((resolve, reject) => {
            return this.httpClient
                .get<User>(environment.webserviceUrl + '/v1/users/me')
                .toPromise()
                .then((user) => {
                    user = this.userConverter.convert(user);
                    this.storeUser(user);
                    resolve(user);
                })
                .catch((e) => reject(e));
        });

    }

    private storeUser(user: User) {
        this.setStorageValue(AuthService.STORAGE_KEY_USER, JSON.stringify(user));
        (this.user as BehaviorSubject<User>).next(user);
    }

    private storeAccount(account: Account) {
        account = new AccountConverter().convert(account);
        this.setStorageValue(AuthService.STORAGE_KEY_ACCOUNT, JSON.stringify(account));
        (this.account as BehaviorSubject<Account>).next(account);
    }

    private loadAccount(): Promise<Account> {
        return new Promise<Account>((resolve, reject) => {
            return this.accountService.getCurrent()
                .toPromise()
                .then((account) => {
                    this.storeAccount(account);
                    resolve(account);
                })
                .catch((e) => reject(e));
        });
    }

    private readToken(token, storeToken: boolean): JwtTokenModel {
        const tokenObject = JwtTokenModel.fromJson(token);
        (this.tokenContent$ as BehaviorSubject<JwtTokenModel>).next(tokenObject);
        if (storeToken) {
            this.setStorageValue(AuthService.STORAGE_KEY_TOKEN, token);
            (this.tokenChanged$ as BehaviorSubject<boolean>).next(true);
        }

        console.log(['tokenObject', tokenObject]);
        // Update applications
        const applications: AuthApplicationModel[] = [];

        tokenObject.subscriptions.forEach(subscription => {
            const application = allApplications.find(value => value.serviceValue === subscription.service.value);
            if (application) {
                let toBeAdded = true;
                if (application.code === Applications.INSOLE_SUBCONTRACT_APP) {
                    if (tokenObject.subscriptions.find(value => value.service.value === ApplicationInsoleSelfProduction.serviceValue)) {
                        toBeAdded = false;
                    }
                }
                if (toBeAdded && applications.findIndex(value1 => value1.code === application.code) === -1) {
                    applications.push(application);
                }
            }

        });
        (this.applications$ as BehaviorSubject<AuthApplicationModel[]>).next(applications
            .sort((a, b) => a.index - b.index));
        return tokenObject;
    }

    updateUser(user: User) {
        (this.user as BehaviorSubject<User>).next(user);
    }

    get touchable(): boolean {
        return window.localStorage.getItem('touchable') === 'true';
    }
}
